import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import { CameraAlt as SwitchCameraIcon } from '@mui/icons-material';
import { Html5Qrcode, CameraDevice } from 'html5-qrcode';
import { qrcodeService } from '../services';
import { SparePart } from './SpareParts';

const QRScanner: React.FC = () => {
  const [scanning, setScanning] = useState(false);
  const [scannedPart, setScannedPart] = useState<SparePart | null>(null);
  const [error, setError] = useState('');
  const [availableCameras, setAvailableCameras] = useState<CameraDevice[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const qrReaderRef = useRef<HTMLDivElement>(null);

  const getStockColor = (part: SparePart): "error" | "warning" | "success" => {
    if (part.current_stock <= part.minimum_stock) return 'error';
    if (part.reorder_point && part.current_stock <= part.reorder_point) return 'warning';
    return 'success';
  };

  useEffect(() => {
    // Load available cameras on mount
    loadCameras();
    return () => {
      stopScanning();
    };
  }, []);

  const loadCameras = async () => {
    try {
      const devices = await Html5Qrcode.getCameras();
      if (devices && devices.length > 0) {
        console.log('Available cameras:', devices.map(d => ({ id: d.id, label: d.label })));
        
        // Sort cameras: prefer back cameras without "wide" or "ultra" in the name
        const sortedCameras = [...devices].sort((a, b) => {
          const aLabel = a.label.toLowerCase();
          const bLabel = b.label.toLowerCase();
          
          const aIsBack = aLabel.includes('back') || aLabel.includes('rear') || aLabel.includes('environment');
          const bIsBack = bLabel.includes('back') || bLabel.includes('rear') || bLabel.includes('environment');
          
          const aIsWide = aLabel.includes('wide') || aLabel.includes('ultra') || aLabel.includes('0.5');
          const bIsWide = bLabel.includes('wide') || bLabel.includes('ultra') || bLabel.includes('0.5');
          
          // Back cameras first
          if (aIsBack && !bIsBack) return -1;
          if (!aIsBack && bIsBack) return 1;
          
          // Among back cameras, non-wide first
          if (aIsBack && bIsBack) {
            if (!aIsWide && bIsWide) return -1;
            if (aIsWide && !bIsWide) return 1;
          }
          
          return 0;
        });
        
        setAvailableCameras(sortedCameras);
        console.log('Sorted cameras:', sortedCameras.map(d => ({ id: d.id, label: d.label })));
      }
    } catch (err) {
      console.error('Failed to load cameras:', err);
    }
  };

  const startScanning = async (cameraIndex: number = currentCameraIndex) => {
    try {
      setError('');
      setScannedPart(null);

      const html5QrCode = new Html5Qrcode('qr-reader');
      scannerRef.current = html5QrCode;

      // Use the selected camera or fall back to facingMode
      const cameraId = availableCameras[cameraIndex]?.id;
      
      console.log('Starting camera:', cameraId, availableCameras[cameraIndex]?.label);

      await html5QrCode.start(
        cameraId || { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        onScanSuccess,
        onScanFailure
      );

      setScanning(true);
      setCurrentCameraIndex(cameraIndex);
    } catch (err: any) {
      setError('Failed to start camera: ' + err.message);
      console.error(err);
    }
  };

  const switchCamera = async () => {
    if (availableCameras.length <= 1) return;
    
    await stopScanning();
    const nextIndex = (currentCameraIndex + 1) % availableCameras.length;
    setTimeout(() => startScanning(nextIndex), 300);
  };

  const stopScanning = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        scannerRef.current = null;
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText: string) => {
    try {
      stopScanning();
      const response = await qrcodeService.scan(decodedText);
      setScannedPart(response.part);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch part details');
    }
  };

  const onScanFailure = (err: any) => {
    // Ignore scan failures (happens frequently during scanning)
  };

  const getStockStatus = (part: SparePart) => {
    if (part.current_stock === 0) return 'error';
    if (part.reorder_point && part.current_stock <= part.reorder_point) return 'warning';
    return 'success';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        QR Code Scanner
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Scan QR Code
            </Typography>

            <Box sx={{ mt: 2 }}>
              <div
                id="qr-reader"
                ref={qrReaderRef}
                style={{ width: '100%', maxWidth: 500, margin: '0 auto' }}
              />
            </Box>

            <Box sx={{ mt: 3, textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
              {!scanning ? (
                <Button variant="contained" size="large" onClick={() => startScanning()}>
                  Start Scanning
                </Button>
              ) : (
                <>
                  <Button variant="outlined" size="large" onClick={stopScanning} color="error">
                    Stop Scanning
                  </Button>
                  {availableCameras.length > 1 && (
                    <Tooltip title="Switch Camera">
                      <IconButton onClick={switchCamera} color="primary" size="large">
                        <SwitchCameraIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>

            {availableCameras.length > 0 && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                  Camera: {availableCameras[currentCameraIndex]?.label || 'Default'} 
                  ({currentCameraIndex + 1}/{availableCameras.length})
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Scanned Part Details
            </Typography>

            {scannedPart ? (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {scannedPart.part_name}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography color="textSecondary" variant="body2" fontWeight="bold">
                          Part Number
                        </Typography>
                        <Typography variant="body1">{scannedPart.part_number}</Typography>
                      </Grid>

                      {scannedPart.sap_number && (
                        <Grid item xs={12} sm={6}>
                          <Typography color="textSecondary" variant="body2" fontWeight="bold">
                            SAP Number
                          </Typography>
                          <Typography variant="body1">{scannedPart.sap_number}</Typography>
                        </Grid>
                      )}

                      {scannedPart.manufacturer_part_number && (
                        <Grid item xs={12} sm={6}>
                          <Typography color="textSecondary" variant="body2" fontWeight="bold">
                            Manufacturer Part No.
                          </Typography>
                          <Typography variant="body1">{scannedPart.manufacturer_part_number}</Typography>
                        </Grid>
                      )}

                      <Grid item xs={12} sm={6}>
                        <Typography color="textSecondary" variant="body2" fontWeight="bold">
                          Status
                        </Typography>
                        <Chip
                          label={scannedPart.status}
                          size="small"
                          color={scannedPart.status === 'active' ? 'success' : 'default'}
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography color="textSecondary" variant="body2" fontWeight="bold" gutterBottom>
                          Stock Information
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Box>
                            <Typography variant="caption" color="textSecondary">Total Stock</Typography>
                            <Chip
                              label={scannedPart.current_stock}
                              color={getStockColor(scannedPart)}
                              sx={{ ml: 1 }}
                            />
                          </Box>
                          {scannedPart.pds_stock !== undefined && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">PDS Stock</Typography>
                              <Chip
                                label={scannedPart.pds_stock}
                                variant="outlined"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            </Box>
                          )}
                          {scannedPart.ems_stock !== undefined && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">EMS Stock</Typography>
                              <Chip
                                label={scannedPart.ems_stock}
                                variant="outlined"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            </Box>
                          )}
                          <Box>
                            <Typography variant="caption" color="textSecondary">Min Stock</Typography>
                            <Chip
                              label={scannedPart.minimum_stock}
                              variant="outlined"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      {scannedPart.unit_price && (
                        <Grid item xs={6}>
                          <Typography color="textSecondary" variant="body2" fontWeight="bold">
                            Unit Price
                          </Typography>
                          <Typography variant="body1">
                            ${Number(scannedPart.unit_price || 0).toFixed(2)}
                          </Typography>
                        </Grid>
                      )}

                      <Grid item xs={6}>
                        <Typography color="textSecondary" variant="body2" fontWeight="bold">
                          Unit of Measure
                        </Typography>
                        <Typography variant="body1">{scannedPart.unit_of_measure || 'pieces'}</Typography>
                      </Grid>

                      {scannedPart.category_name && (
                        <Grid item xs={12} sm={6}>
                          <Typography color="textSecondary" variant="body2" fontWeight="bold">
                            Category
                          </Typography>
                          <Typography variant="body1">{scannedPart.category_name}</Typography>
                        </Grid>
                      )}

                      {scannedPart.machine_names && (
                        <Grid item xs={12} sm={6}>
                          <Typography color="textSecondary" variant="body2" fontWeight="bold">
                            Machines
                          </Typography>
                          <Typography variant="body1">{scannedPart.machine_names}</Typography>
                        </Grid>
                      )}

                      {scannedPart.supplier_name && (
                        <Grid item xs={12}>
                          <Typography color="textSecondary" variant="body2" fontWeight="bold">
                            Supplier
                          </Typography>
                          <Typography variant="body1">{scannedPart.supplier_name}</Typography>
                        </Grid>
                      )}

                      {scannedPart.location && (
                        <Grid item xs={6}>
                          <Typography color="textSecondary" variant="body2" fontWeight="bold">
                            Location
                          </Typography>
                          <Typography variant="body1">{scannedPart.location}</Typography>
                        </Grid>
                      )}

                      {scannedPart.shelf_position && (
                        <Grid item xs={6}>
                          <Typography color="textSecondary" variant="body2" fontWeight="bold">
                            Shelf Position
                          </Typography>
                          <Typography variant="body1">{scannedPart.shelf_position}</Typography>
                        </Grid>
                      )}

                      {scannedPart.description && (
                        <Grid item xs={12}>
                          <Typography color="textSecondary" variant="body2" fontWeight="bold">
                            Description
                          </Typography>
                          <Typography variant="body1">{scannedPart.description}</Typography>
                        </Grid>
                      )}

                      {scannedPart.part_use && (
                        <Grid item xs={12}>
                          <Typography color="textSecondary" variant="body2" fontWeight="bold">
                            Use
                          </Typography>
                          <Typography variant="body1">{scannedPart.part_use}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Button variant="contained" fullWidth onClick={() => setScannedPart(null)}>
                      Scan Another
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Box
                sx={{
                  mt: 3,
                  p: 4,
                  textAlign: 'center',
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                }}
              >
                <Typography color="textSecondary">
                  Scan a QR code to view part details
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QRScanner;
